export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

export interface TimezoneOption {
  readonly value: string;
  readonly label: string;
  readonly offset: string;
}

export const BRAZIL_TIMEZONES: readonly TimezoneOption[] = [
  {
    value: 'America/Noronha',
    label: 'Brasil - Fernando de Noronha',
    offset: 'UTC-2',
  },
  {
    value: 'America/Sao_Paulo',
    label: 'Brasil - São Paulo / Brasília',
    offset: 'UTC-3',
  },
  {
    value: 'America/Fortaleza',
    label: 'Brasil - Fortaleza / Nordeste',
    offset: 'UTC-3',
  },
  { value: 'America/Recife', label: 'Brasil - Recife', offset: 'UTC-3' },
  { value: 'America/Belem', label: 'Brasil - Belém', offset: 'UTC-3' },
  { value: 'America/Manaus', label: 'Brasil - Manaus', offset: 'UTC-4' },
  { value: 'America/Cuiaba', label: 'Brasil - Cuiabá', offset: 'UTC-4' },
  {
    value: 'America/Campo_Grande',
    label: 'Brasil - Campo Grande',
    offset: 'UTC-4',
  },
  {
    value: 'America/Porto_Velho',
    label: 'Brasil - Porto Velho',
    offset: 'UTC-4',
  },
  { value: 'America/Boa_Vista', label: 'Brasil - Boa Vista', offset: 'UTC-4' },
  {
    value: 'America/Rio_Branco',
    label: 'Brasil - Rio Branco / Acre',
    offset: 'UTC-5',
  },
];

const BRAZIL_VALUES = new Set(BRAZIL_TIMEZONES.map((item) => item.value));

function getOffsetLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value;
    return offset?.replace('GMT', 'UTC') ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

function buildLabel(timeZone: string): string {
  const offset = getOffsetLabel(timeZone);
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
  return `${city} (${offset})`;
}

export function timezoneSelectLabel(timeZone: string): string {
  const brazil = BRAZIL_TIMEZONES.find((item) => item.value === timeZone);
  if (brazil) {
    return `${brazil.label} (${brazil.offset})`;
  }

  return buildLabel(timeZone);
}

export function buildAllTimezoneOptions(): readonly TimezoneOption[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : BRAZIL_TIMEZONES.map((item) => item.value);

  return supported
    .filter((value) => !BRAZIL_VALUES.has(value))
    .map((value) => ({
      value,
      label: buildLabel(value),
      offset: getOffsetLabel(value),
    }))
    .sort((left, right) => left.label.localeCompare(right.label, 'pt-BR'));
}

export function isValidTimezone(value: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function normalizeTimezone(value?: string | null): string {
  if (value && isValidTimezone(value)) {
    return value;
  }

  return DEFAULT_TIMEZONE;
}
